import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Lightbulb } from "lucide-react";
import { useFeatureRequestTopics } from "../hooks/useFeatureRequestTopics";
import { apiService, extractApiError } from "../services/api";
import { FeatureRequest } from "../types";
import MessageCard from "./MessageCard";

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 5000;
const TITLE_MIN = 5;
const DESCRIPTION_MIN = 10;
// Only search for similar requests once the title looks like a real phrase, to
// avoid the suggestion popping up on the first few keystrokes.
const SIMILAR_MIN_CHARS = 10;
const SIMILAR_DEBOUNCE_MS = 400;

interface FeatureRequestFormProps {
  /** When provided, the form edits the given request instead of creating one. */
  request?: FeatureRequest | null;
  onSuccess?: (request: FeatureRequest) => void;
  onCancel?: () => void;
}

export default function FeatureRequestForm({
  request,
  onSuccess,
  onCancel,
}: FeatureRequestFormProps) {
  const isEdit = Boolean(request);
  const { topics } = useFeatureRequestTopics();
  const [title, setTitle] = useState(request?.title ?? "");
  const [description, setDescription] = useState(request?.description ?? "");
  const [category, setCategory] = useState(request?.category ?? "");
  const [debouncedTitle, setDebouncedTitle] = useState("");
  const [debouncedDescription, setDebouncedDescription] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedTitle(title.trim());
      setDebouncedDescription(description.trim());
    }, SIMILAR_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [title, description]);

  const similarEnabled = !isEdit && debouncedTitle.length >= SIMILAR_MIN_CHARS;

  const similarQuery = useQuery<FeatureRequest[]>(
    ["feature-requests-similar", debouncedTitle, debouncedDescription],
    async () => {
      const res = await apiService.getSimilarFeatureRequests(
        debouncedTitle,
        debouncedDescription,
      );
      return (res.data?.data?.items ?? []) as FeatureRequest[];
    },
    {
      enabled: similarEnabled,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const similarRequests = similarEnabled ? (similarQuery.data ?? []) : [];

  const mutation = useMutation(
    () =>
      isEdit && request
        ? apiService.updateOwnFeatureRequest(
            request.id,
            title.trim(),
            description.trim(),
          )
        : apiService.submitFeatureRequest(
            title.trim(),
            description.trim(),
            category,
          ),
    {
      onSuccess: (res) => {
        if (res.data?.success && res.data.data) {
          if (!isEdit) {
            setTitle("");
            setDescription("");
            setCategory("");
          }
          queryClient.invalidateQueries("feature-requests");
          onSuccess?.(res.data.data as FeatureRequest);
        }
      },
    },
  );

  const canSubmit =
    title.trim().length >= TITLE_MIN &&
    description.trim().length >= DESCRIPTION_MIN &&
    (isEdit || category !== "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate();
  };

  const errorMessage = mutation.isError
    ? extractApiError(mutation.error)
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="featureRequestTitle"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Title <span className="text-red-500">*</span>
        </label>
        <input
          id="featureRequestTitle"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input"
          placeholder="Short summary of the feature you would like"
          maxLength={TITLE_MAX}
          disabled={mutation.isLoading}
          required
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {title.trim().length > 0 && title.trim().length < TITLE_MIN
            ? `Add ${TITLE_MIN - title.trim().length} more character${
                TITLE_MIN - title.trim().length === 1 ? "" : "s"
              } — titles need at least ${TITLE_MIN} characters.`
            : `Use at least ${TITLE_MIN} characters.`}
        </p>
      </div>

      {!isEdit && (
        <div>
          <label
            htmlFor="featureRequestCategory"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Category <span className="text-red-500">*</span>
          </label>
          <select
            id="featureRequestCategory"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input"
            disabled={mutation.isLoading}
            required
          >
            <option value="" disabled>
              Select a category
            </option>
            {topics.map((option) => (
              <option key={option.slug} value={option.slug}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {similarRequests.length > 0 && (
        <MessageCard icon={Lightbulb} title="Similar requests already exist">
          Someone may already have asked for this. Have a look at the Feature
          requests tab before submitting a duplicate:
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {similarRequests.map((similar) => (
              <li key={similar.id} className="font-medium">
                {similar.title}
              </li>
            ))}
          </ul>
        </MessageCard>
      )}

      <div>
        <label
          htmlFor="featureRequestDescription"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="featureRequestDescription"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input min-h-[8rem]"
          placeholder="What would you like to see, and why would it help you?"
          maxLength={DESCRIPTION_MAX}
          disabled={mutation.isLoading}
          required
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {description.trim().length > 0 &&
          description.trim().length < DESCRIPTION_MIN
            ? `Add ${DESCRIPTION_MIN - description.trim().length} more character${
                DESCRIPTION_MIN - description.trim().length === 1 ? "" : "s"
              } — descriptions need at least ${DESCRIPTION_MIN} characters.`
            : `Use at least ${DESCRIPTION_MIN} characters so we understand your idea.`}
        </p>
      </div>

      {errorMessage && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {errorMessage}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={mutation.isLoading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={mutation.isLoading || !canSubmit}
        >
          {mutation.isLoading
            ? isEdit
              ? "Saving..."
              : "Submitting..."
            : isEdit
              ? "Save changes"
              : "Submit feature request"}
        </button>
      </div>
    </form>
  );
}
